import { z } from "zod";

export const accountRoleSchema = z.enum(["owner", "admin"]).nullable();
export const workspaceRoleSchema = z.enum(["admin", "manager", "member"]);

/**
 * Shared password constraints. Mirrors shore's PASSWORD_MIN_LENGTH (8) /
 * PASSWORD_MAX_LENGTH (200) in auth.dto.ts. Single source so signup, reset,
 * and change all agree on the rule and the copy.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 200;
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
  .max(PASSWORD_MAX_LENGTH, `Keep it under ${PASSWORD_MAX_LENGTH} characters.`);

export const publicUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  accountRole: accountRoleSchema,
  // M2 additions (all optional, pre-M2 rows lack these fields)
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  nickName: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  preferredCurrency: z.string().nullable().optional(),
  // Whether the user has a password set (drives the profile Security card copy).
  // Only present on /auth/me.
  hasPassword: z.boolean().optional(),
});
export type PublicUser = z.infer<typeof publicUserSchema>;

export const updateProfileResponseSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  nickName: z.string().nullable(),
  timezone: z.string().nullable(),
  preferredCurrency: z.string().nullable(),
});
export type UpdateProfileResponse = z.infer<typeof updateProfileResponseSchema>;

export const publicAccountSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type PublicAccount = z.infer<typeof publicAccountSchema>;

export const publicWorkspaceSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  name: z.string(),
  slug: z.string(),
  memberCount: z.number().int().nonnegative(),
  isDemo: z.boolean().default(false),
  isReadOnly: z.boolean().default(false),
  /**
   * Does this workspace run hiring through requisitions and staffing plans?
   *
   * A UI-shaping flag rather than a settings read: it rides on `/auth/me` so
   * that a hiring manager, who gets a 403 from `/workspaces/:id/hr-settings`,
   * can still be spared a surface built on a word their company does not use.
   *
   * `.default(true)` is the contract, not a guess. The shore's column defaults
   * to true and only `/auth/me` populates the field at all, so an absent value
   * means "this payload does not carry the flag", and the honest reading of
   * that is the default every workspace starts on. Defaulting to false here
   * would switch governance off for every workspace the moment a deploy
   * skewed, which is a silently missing feature rather than a visible one.
   */
  hiringGovernanceEnabled: z.boolean().default(true),
  /// Calendar date this workspace treats as today. Absent or null means the
  /// real clock. Only `/auth/me` sends it.
  operatingDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
});
export type PublicWorkspace = z.infer<typeof publicWorkspaceSchema>;

export const publicMembershipSchema = z.object({
  workspaceId: z.string(),
  role: workspaceRoleSchema,
});
export type PublicMembership = z.infer<typeof publicMembershipSchema>;

/**
 * PAYD-FR-042 (payroll depth H-03). A workspace the caller has LEFT and can
 * still reach their own pay records in, for as long as the window is open.
 *
 * Deliberately thin, and this is the shore's shape rather than a projection
 * of it: no name, no slug, no role, because an alumni session carries none of
 * the workspace reads a member's does. There is nothing here to build a
 * switcher, a sidebar or a colleague list out of, which is the point.
 *
 * The shore only sends bindings whose window is still open, so an entry here
 * means access today. `.default([])` keeps a shore that has not shipped H-03
 * from failing the whole `/auth/me` parse.
 */
export const publicAlumniMembershipSchema = z.object({
  workspaceId: z.string(),
  mode: z.literal("alumni"),
  /// ISO-8601. The moment the access window closes.
  accessUntil: z.string(),
});
export type PublicAlumniMembership = z.infer<typeof publicAlumniMembershipSchema>;

export const meResponseSchema = z.object({
  user: publicUserSchema,
  account: publicAccountSchema,
  workspaces: z.array(publicWorkspaceSchema),
  memberships: z.array(publicMembershipSchema),
  alumniMemberships: z.array(publicAlumniMembershipSchema).default([]),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const signupRequestResponseSchema = z.object({ sent: z.literal(true) });
export const signupRequestSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  email: z.string().email(),
  password: passwordSchema,
});
export type SignupRequestInput = z.infer<typeof signupRequestSchema>;

// --- Password login + reset --------------------------------------------------

export const loginPasswordSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, "Enter your password."),
});
export type LoginPasswordInput = z.infer<typeof loginPasswordSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const signupVerifyResponseSchema = z.object({
  token: z.string(),
  accepted: z.literal(true),
  email: z.string().email(),
  intent: z.literal("signup"),
});
export type SignupVerifyResponse = z.infer<typeof signupVerifyResponseSchema>;

export const signupCompleteResponseSchema = z.object({
  user: publicUserSchema,
  account: publicAccountSchema,
  workspace: publicWorkspaceSchema,
});
export const signupCompleteRequestSchema = z.object({
  token: z.string().min(1),
});
export type SignupCompleteInput = z.infer<typeof signupCompleteRequestSchema>;
export type SignupCompleteResponse = z.infer<typeof signupCompleteResponseSchema>;
